<template>
  <div class="dashboard-component-container">
    <checklist
      v-if="showFirstTimeChecklist"
      :checklist="firstTimeChecklist"
    />
    <div
      v-if="leftDashboardWidget.type"
      :class="{wide: !rightDashboardWidget.type}"
      class="top-widgets"
    >
      <component
        :is="leftDashboardWidget.type"
        :label="leftDashboardWidget.label"
      />
      <component
        :is="rightDashboardWidget.type"
        v-if="rightDashboardWidget.type"
        :label="rightDashboardWidget.label"
      />
    </div>
    <div
      v-if="dashboardItems.length"
      class="dashboard-items"
    >
      <div class="content-panel">
        <div class="heading">
          {{ $store.getters['app/dashboardLabels'].left }}
        </div>
        <div class="item-container">
          <router-link
            v-for="item in leftItems"
            :key="item.id"
            :to="item.route"
          >
            <icon
              :name="item.webIcon"
              variant="354052"
            />
            <div class="name">
              {{ item.name }}
            </div>
          </router-link>
        </div>
      </div>
      <template v-if="$store.getters['app/dashboardLabels'].right">
        <div class="panel-spacer" />
        <div class="content-panel">
          <div class="heading">
            {{ $store.getters['app/dashboardLabels'].right }}
          </div>
          <div class="item-container">
            <router-link
              v-for="item in rightItems"
              :key="item.id"
              :to="item.route"
            >
              <icon
                :name="item.webIcon"
                variant="354052"
              />
              <div class="name">
                {{ item.name }}
              </div>
            </router-link>
          </div>
        </div>
      </template>
    </div>
    <div
      v-if="showPerformanceWidgets"
      class="widget-container"
    >
      <widgets />
    </div>
    <message
      v-if="sponsorInfo"
      :label="sponsorInfo.label"
      :content="sponsorInfo.content"
      class="message-block"
    />
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';
import { asyncImport } from '@/utils';
const Widgets = defineAsyncComponent(() => asyncImport(import('@components/progress/widgets/index')));
import Activation from '@components/widgets/activation';
import Checklist from '@components/dashboard-widgets/checklist';
import EmojiRating from '@components/widgets/emojiRating';
import Iowacdl from '@components/widgets/iowacdl';
import PickUp from '@components/widgets/pickUp';
import Notes from '@components/widgets/notes';
import Countdown from '@components/widgets/countdown';
import Message from '@components/widgets/message';
import Simulations from '@components/widgets/simulations';
import Icon from '@components/singletons/icon.vue';
import { shouldEnterTour, showTourModal } from '@/utils/tour';
import { getChecklist, hasChecklist, ONBOARDING_CHECKLIST_KEY } from '@/utils/checklist';


export default {
  components: {
    Activation,
    Checklist,
    Countdown,
    EmojiRating,
    Iowacdl,
    Message,
    Notes,
    PickUp,
    Simulations,
    Icon,
    Widgets,
  },
  computed: {
    ...mapGetters('navigationItems', [
      'dashboardItems',
      'menuItems',
    ]),
    firstTimeChecklist() {
      return getChecklist(this.$store, ONBOARDING_CHECKLIST_KEY);
    },
    showFirstTimeChecklist() {
      return hasChecklist(this.$store, this.firstTimeChecklist);
    },
    leftDashboardWidget() {
      if (this.$store.getters['app/dashboardWidgets'].left && this.$store.getters['app/dashboardWidgets'].left.type) {
        return this.$store.getters['app/dashboardWidgets'].left;
      } else {
        return {};
      }
    },
    rightDashboardWidget() {
      if (this.$store.getters['app/dashboardWidgets'].right && this.$store.getters['app/dashboardWidgets'].right.type) {
        return this.$store.getters['app/dashboardWidgets'].right;
      } else {
        return {};
      }
    },
    leftItems() {
      if (this.$store.getters['app/dashboardLabels'].right) {
        return this.dashboardItems.slice(0, 2);
      } else {
        return this.dashboardItems;
      }
    },
    rightItems() {
      if (this.$store.getters['app/dashboardLabels'].right) {
        return this.dashboardItems.slice(2, 4);
      } else {
        return [];
      }
    },
    showPerformanceWidgets() {
      return this.menuItems.find(i => i.destination === 'progress');
    },
    sponsorInfo() {
      const nameExists = this.$store.getters['productVars/get']['sponsor_name'];
      const descriptionExists = this.$store.getters['productVars/get']['sponsor_description'];
      if (!nameExists || !descriptionExists) {
        return false;
      }
      return {label: nameExists.value, content: descriptionExists.value};
    },
  },
  created() {
    this.$store.dispatch('flashcardStates/fetchStates', ['categories', 'flashcards', 'mnemonics', 'stats']);
  },
  mounted() {
    // if user should start tour, launch tour
    if (shouldEnterTour(this.$store)) {
      showTourModal.apply(this);
    }
  },
};

</script>

<style lang='scss' scoped>
  .dashboard-component-container {
    background-color: $blue-2;
    padding: rem-calc(48) !important;

    @include breakpoint(small) {
      padding: rem-calc(16) !important;
    }

    .checklist-container {
      margin: rem-calc(-48) 0 rem-calc(48) rem-calc(-48);

      @include breakpoint(small) {
        margin: rem-calc(-16) 0 rem-calc(16) rem-calc(-16);
      }
    }

    .dashboard-items {
      display: flex;

      @include breakpoint(small) {
        flex-direction: column;
      }

      .panel-spacer {
        height: rem-calc(24);
        width: rem-calc(24);

        @include breakpoint(small) {
          height: rem-calc(16);
        }
      }

      .content-panel {
        border-right: none;
        border-bottom: none;
        border-left: none;
        box-sizing: border-box;
        box-shadow: 0 1px 2px 0 rgba(0,0,0,0.25);
        flex: 1;

        .heading {
          color: #354052;
          font-size: rem-calc(22);
          margin-bottom: rem-calc(24);
        }

        .item-container {
          display: flex;
          margin-bottom: rem-calc(24);

          @include breakpoint(small) {
            flex-wrap: wrap;
            margin-bottom: rem-calc(16);
          }

          > a {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;

            @include breakpoint(small) {
              flex-basis: 50%;
              margin: rem-calc(16) 0;
            }

            .name {
              color: #354052;
              text-align: center;

              font: {
                size: rem-calc(14);
                weight: $semi-bold;
              }
            }

            img {
              margin-bottom: rem-calc(24);
              width: rem-calc(64);
            }
          }
        }
      }
    }

    .widget-container {
      margin-top: rem-calc(24);

      @include breakpoint(small) {
        margin-top: rem-calc(16);
      }
    }

    .message-block {
      margin-top: rem-calc(30);
    }

    .top-widgets {
      display: flex;

      > div {
        background: white;
        border-radius: (rem-calc(4));
        box-shadow: 0 1px 2px 0 rgba(0,0,0,0.25);
        flex: 1;
        margin-bottom: rem-calc(24);

        &:first-child {
          margin-right: rem-calc(12);
        }

        &:last-child {
          margin-left: rem-calc(12);
        }

        @include breakpoint(medium) {
          margin-left: 0 !important;
          margin-right: 0 !important;
          margin-bottom: rem-calc(16);
        }
      }

      @include breakpoint(medium) {
        flex-direction: column;
      }

      &.wide > div {
        margin-left: 0;
        margin-right: 0;
      }
    }
  }
</style>
